/**
 * Internal helper to pad numbers for date formatting
 *
 * @param {number | string} segment
 */
const padTime = segment => segment.toString().padStart(2, '0');

/**
 * Takes in a string in **HH:MM:SS** format and parses it to seconds
 *
 * TODO: really we should save times as proper time object
 * but this will do in the mean time.
 *
 * @param {string} hms
 * */
export const parseHMS = hms => {
  // eslint-disable-next-line id-length -- hms
  const [h = 0, m = 0, s = 0] = hms.split(':').map(Number);

  return h * 3600 + m * 60 + s;
};

/**
 * Takes in `Date` object and returns a string in format **HH:MM:SS**
 *
 * @param {Date} date
 * */
export const dateToHMS = date =>
  date.toLocaleTimeString('en', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

/**
 * Returns the capitalized full weekday name (e.g. "Friday"/"Vendredi") for the given
 * ISO weekday (0 = Sunday, 1 = Monday, ..., 6 = Saturday), in the given locale.
 *
 * Uses a fixed reference date (2025-09-07 is a Sunday) so callers only need to pass an isoWeekday.
 *
 * @param {number} isoWeekday
 * @param {string} locale
 */
export const weekdayLongLabel = (isoWeekday, locale) => {
  const label = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(
    new Date(2025, 8, isoWeekday),
  );

  return `${label.charAt(0).toUpperCase()}${label.slice(1)}`;
};

export class TimeSpan {
  /**
   * @param {number | Date} spanInMSOrStartDate
   * @param {Date} [endDate]
   */
  constructor(spanInMSOrStartDate, endDate) {
    let spanInMs;
    if (typeof spanInMSOrStartDate === 'number') {
      spanInMs = spanInMSOrStartDate;
      if (endDate) {
        throw new TypeError(
          'First param is a number, assuming timespan in ms, but received an endDate',
        );
      }
    } else {
      spanInMs = endDate.getTime() - spanInMSOrStartDate.getTime();
    }
    if (spanInMs < 0) {
      throw new RangeError(`Negative timespans unsupported. Got: ${spanInMs}`);
    }

    const totalSeconds = Math.floor(spanInMs / 1000);
    this.hours = Math.floor(totalSeconds / 3600);
    this.minutes = Math.floor((totalSeconds % 3600) / 60);
    this.seconds = totalSeconds % 60;
    this.milliseconds = spanInMs % 1000;
  }

  format = ({ hours = true, milliseconds = false } = {}) => {
    let formatted = '';
    if (hours) formatted += `${padTime(this.hours)}:`;
    formatted += `${padTime(this.minutes)}:${padTime(this.seconds)}`;
    if (milliseconds) formatted += `.${this.milliseconds.toString().padStart(3, '0')}:`;

    return formatted;
  };
}
